import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { CheckCircle, Clock, Circle, ArrowLeft } from "lucide-react";
import { useTheme } from "../context/ThemeContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS = {
  fulfilled: { label: "Fulfilled", bg: "#10B981", text: "#fff" },
  in_progress: { label: "In Progress", bg: "#F59E0B", text: "#000" },
  pending: { label: "Pending", bg: "#6B7280", text: "#fff" },
};

const STATUS_ICONS = {
  fulfilled: CheckCircle,
  in_progress: Clock,
  pending: Circle,
};

const PARTY_FILTERS = [
  { id: "all", name: "All Parties" },
  { id: "alp", name: "Labor" },
  { id: "liberal", name: "Liberal" },
  { id: "greens", name: "Greens" },
  { id: "teal", name: "Teal" },
];

const STATUS_FILTERS = [
  { id: "all", name: "All" },
  { id: "fulfilled", name: "Fulfilled" },
  { id: "in_progress", name: "In Progress" },
  { id: "pending", name: "Pending" },
];

const PARTY_COLORS = {
  alp: "#CC2529", liberal: "#1B4F9E", greens: "#10B981",
  nationals: "#155E3B", teal: "#0D9488", "one-nation": "#D97706",
};

export default function ScorecardPage() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [promises, setPromises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [partyFilter, setPartyFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const bg = isDark ? "#0F172A" : "#F9F7F2";
  const surface = isDark ? "#1E293B" : "#FFFFFF";
  const text = isDark ? "#F8FAFC" : "#0F172A";
  const textSec = isDark ? "#94A3B8" : "#475569";

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (partyFilter !== "all") params.append("party_id", partyFilter);
    if (statusFilter !== "all") params.append("status", statusFilter);
    axios
      .get(`${API}/scorecard?${params}`)
      .then((r) => setPromises(r.data))
      .catch(() => setPromises([]))
      .finally(() => setLoading(false));
  }, [partyFilter, statusFilter]);

  const stats = {
    fulfilled: promises.filter((p) => p.status === "fulfilled").length,
    in_progress: promises.filter((p) => p.status === "in_progress").length,
    pending: promises.filter((p) => p.status === "pending").length,
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: bg }} data-testid="scorecard-page">
      {/* Hero */}
      <div className="py-12 px-4 border-b-2 border-black" style={{ backgroundColor: "#0F172A" }}>
        <div className="max-w-5xl mx-auto">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-1.5 text-sm font-bold mb-5 hover:opacity-75 transition-opacity"
            style={{ color: "#FFC82E" }}
          >
            <ArrowLeft className="w-4 h-4" /> Home
          </button>
          <h1
            className="text-4xl sm:text-5xl font-black text-white mb-2"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Policy Scorecard
          </h1>
          <p className="text-sm mb-6" style={{ color: "#94A3B8" }}>
            Tracking party promises vs. actual delivery — updated for 2026.
          </p>
          <div className="flex flex-wrap gap-3">
            {Object.entries(stats).map(([key, count]) => {
              const s = STATUS[key];
              return (
                <div
                  key={key}
                  className="border-2 border-black px-4 py-2 flex items-center gap-2"
                  style={{ backgroundColor: s.bg + "25" }}
                >
                  <span className="text-xl font-black" style={{ color: s.bg }}>{count}</span>
                  <span className="text-xs font-bold text-white">{s.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Filters */}
        <div className="flex flex-wrap gap-y-2 gap-x-4 mb-8">
          <div className="flex flex-wrap gap-1">
            {PARTY_FILTERS.map((p) => (
              <button
                key={p.id}
                data-testid={`scorecard-party-filter-${p.id}`}
                onClick={() => setPartyFilter(p.id)}
                className="px-3 py-1.5 text-xs font-bold border-2 border-black transition-all"
                style={{
                  backgroundColor: partyFilter === p.id ? "#FFC82E" : surface,
                  color: partyFilter === p.id ? "#000" : text,
                  boxShadow: partyFilter === p.id ? "2px 2px 0 #000" : "none",
                }}
              >
                {p.name}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-1">
            {STATUS_FILTERS.map((s) => (
              <button
                key={s.id}
                data-testid={`scorecard-status-filter-${s.id}`}
                onClick={() => setStatusFilter(s.id)}
                className="px-3 py-1.5 text-xs font-bold border-2 border-black transition-all"
                style={{
                  backgroundColor: statusFilter === s.id ? "#FFC82E" : surface,
                  color: statusFilter === s.id ? "#000" : text,
                  boxShadow: statusFilter === s.id ? "2px 2px 0 #000" : "none",
                }}
              >
                {s.name}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="border-2 border-black h-36 animate-pulse"
                style={{ backgroundColor: surface }}
              />
            ))}
          </div>
        ) : promises.length === 0 ? (
          <div className="text-center py-20 border-2 border-black" style={{ backgroundColor: surface }}>
            <p className="text-lg font-black mb-1" style={{ color: text }}>No promises found</p>
            <p className="text-sm" style={{ color: textSec }}>Try adjusting your filters.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {promises.map((p) => {
              const s = STATUS[p.status] || STATUS.pending;
              const Icon = STATUS_ICONS[p.status] || Circle;
              const partyColor = PARTY_COLORS[p.party_id] || "#6B7280";
              return (
                <div
                  key={p.id}
                  className="border-2 border-black p-4"
                  style={{ backgroundColor: surface, boxShadow: "3px 3px 0 #000" }}
                  data-testid="scorecard-item"
                >
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <div
                      className="flex items-center gap-1.5 px-2 py-0.5 border border-black text-xs font-black"
                      style={{ backgroundColor: s.bg, color: s.text }}
                    >
                      <Icon className="w-3 h-3" />
                      {s.label}
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className="text-xs font-bold px-2 py-0.5 border border-black"
                        style={{ color: textSec }}
                      >
                        {p.category}
                      </span>
                      <span
                        className="text-xs font-black px-2 py-0.5 border-2 border-black"
                        style={{ backgroundColor: partyColor, color: "#fff" }}
                      >
                        {p.party_id?.toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <p className="font-black text-sm leading-snug mb-2" style={{ color: text }}>
                    {p.promise}
                  </p>
                  {p.notes && (
                    <p className="text-xs leading-relaxed mb-2" style={{ color: textSec }}>
                      {p.notes}
                    </p>
                  )}
                  <p className="text-xs font-medium" style={{ color: textSec }}>
                    Updated: {p.updated_at?.slice(0, 10)}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
